import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import type { Database } from "@/integrations/supabase/types";

type CustomerProviderId = Database["public"]["Enums"]["customer_provider_id"];

export interface RelevanceReviewQueueEncounter {
  encounterId: string;
  pendingCount: number;
  oldestPendingAt: string;
}

export interface UseRelevanceReviewQueueOptions {
  providerIds?: readonly string[];
}

function groupByEncounter(rows: { encounter_id: string; created_at: string }[]): RelevanceReviewQueueEncounter[] {
  const byEncounter = new Map<string, RelevanceReviewQueueEncounter>();
  for (const row of rows) {
    const seen = byEncounter.get(row.encounter_id);
    if (!seen) {
      byEncounter.set(row.encounter_id, {
        encounterId: row.encounter_id,
        pendingCount: 1,
        oldestPendingAt: row.created_at,
      });
      continue;
    }
    seen.pendingCount += 1;
    if (row.created_at < seen.oldestPendingAt) seen.oldestPendingAt = row.created_at;
  }

  return [...byEncounter.values()].sort((a, b) => a.oldestPendingAt.localeCompare(b.oldestPendingAt));
}

async function fetchRelevanceReviewQueue(
  providerIds: readonly string[] = [],
): Promise<RelevanceReviewQueueEncounter[]> {
  const filterByProvider = providerIds.length > 0;

  let query = filterByProvider
    ? supabase
        .from("encounter_relevance_review_items")
        .select(
          `
          encounter_id,
          created_at,
          encounter_relevance_assessments!inner(
            patient_encounters!inner(
              customers!inner(provider_id)
            )
          )
        `,
        )
        .is("reviewed_at", null)
        .in(
          "encounter_relevance_assessments.patient_encounters.customers.provider_id",
          // Generated supabase types lag behind the live customer_provider_id enum (e.g. LNW/THH).
          providerIds as CustomerProviderId[],
        )
    : supabase.from("encounter_relevance_review_items").select("encounter_id, created_at").is("reviewed_at", null);

  query = query.order("created_at", { ascending: true }).limit(500);

  const { data, error } = await query;
  if (error) throw error;
  return groupByEncounter(data ?? []);
}

export function useRelevanceReviewQueue(options?: UseRelevanceReviewQueueOptions) {
  const providerIds = options?.providerIds ?? [];
  const sortedProviderIds = [...providerIds].sort();

  return useQuery({
    queryKey: ["relevance-review-queue", sortedProviderIds],
    queryFn: () => fetchRelevanceReviewQueue(providerIds),
  });
}
